import React from 'react';
import { memoize } from 'lodash';

import {
  dateAdd,
  dateDiff,
  dateSubtract,
  dateMax,
  dateMin,
  dateStartOf,
  dateFormat,
  dateMonthIndex,
  monthYearToDate,
  getMonthIndex,
  getYear,
  dateNow,
  dateIsBefore,
} from 'javascripts/util/dateFns';
import {
  dateToFiscalQuarterString,
  dateToFiscalYearString,
} from 'javascripts/util/fiscalFormats';

/*
 * Helper classes for working with dates in the scale bar (and their
 * pixel/percentage equivalents). Generally, values should be kept as
 * dates for as long as possible (until it's time to render them onto
 * the screen); performing multiple conversions between dates and other
 * units can introduce subtle rounding errors and inaccuracies.
 */

export const clampDate = (date, yearSpan = 20) => {
  const now = dateNow();
  const daysSpan = yearSpan * 365;
  return dateMin(
    dateMax(date, dateSubtract(now, daysSpan, {})),
    dateAdd(now, daysSpan)
  );
};

export const dateToOffset = ({
  startDate,
  endDate,
  width,
  date,
  hideWeekends,
}) => {
  const range = dateDiff(startDate, endDate, { business: hideWeekends });
  const position = dateDiff(startDate, date, { business: hideWeekends });
  return Math.abs((position / range) * width);
};

export const dateToPercentage = ({
  startDate,
  endDate,
  date,
  hideWeekends,
}) => {
  const range = dateDiff(startDate, endDate, { business: hideWeekends });
  const position = dateDiff(startDate, date, { business: hideWeekends });

  return Math.abs((100 * position) / range);
};

const calculateTotalDays = ({ startDate, endDate, hideWeekends }) => {
  return dateDiff(startDate, endDate, { business: hideWeekends });
};

export const getPixelsPerDay = ({
  startDate,
  endDate,
  width,
  hideWeekends = false,
}) => {
  const days = dateDiff(startDate, endDate, { business: hideWeekends });
  return width / days;
};

// Generates the intervals in the form of a start date and a number of days included in the interval
// starting from startDate and ending on endDate.
//
// startOfFirstInterval is used to calculate the start of each interval
// because startDate might be in the middle of an interval
const generateIntervals = ({
  startDate,
  endDate,
  startOfFirstInterval,
  hideWeekends,
  getNextDate,
}) => {
  let startOfInterval = startDate;
  let endOfInterval = getNextDate(startOfFirstInterval);

  const intervals = [];
  while (dateIsBefore(startOfInterval, endDate)) {
    const daysCount = dateDiff(startOfInterval, endOfInterval, {
      business: hideWeekends,
    });

    intervals.push({ startOfInterval, daysCount });

    startOfInterval = endOfInterval;
    endOfInterval = dateMin(getNextDate(startOfInterval), endDate);
  }

  return intervals;
};

export const offsetToDate = ({
  startDate,
  endDate,
  width,
  offset,
  hideWeekends,
}) => {
  const pixelsPerDay = getPixelsPerDay({
    startDate,
    endDate,
    hideWeekends,
    width,
  });

  const numDays = offset / pixelsPerDay;
  return dateAdd(startDate, numDays, { business: hideWeekends });
};

const intervalOptions = ['days', 'weeks', 'months', 'quarters', 'years'];

export const getNormalizedIntervals = params => {
  let { visibleStartDate, visibleEndDate } = params;
  const { visibleWidth, hideWeekends = false, timescale = 'auto' } = params;

  const pixelsPerDay = getPixelsPerDay({
    startDate: visibleStartDate,
    endDate: visibleEndDate,
    width: visibleWidth,
    hideWeekends,
  });

  let minorInterval;
  if (timescale === 'auto') {
    if (pixelsPerDay >= 13) {
      minorInterval = 'days';
    } else if (pixelsPerDay > 3) {
      minorInterval = 'weeks';
    } else if (pixelsPerDay > 1.1) {
      minorInterval = 'months';
    } else {
      minorInterval = 'quarters';
    }
  } else {
    if (['months', 'weeks', 'days'].includes(timescale) && pixelsPerDay < 0.9) {
      minorInterval = 'quarters';
    } else if (['weeks', 'days'].includes(timescale) && pixelsPerDay < 2.1) {
      minorInterval = 'months';
    } else if (timescale === 'days' && pixelsPerDay < 8) {
      minorInterval = 'weeks';
    } else {
      minorInterval = timescale;
    }
  }

  const majorInterval =
    intervalOptions[
      intervalOptions.findIndex(opt => opt === minorInterval) + 1
    ];

  return {
    minor: minorInterval,
    major: majorInterval,
  };
};

function wrapInSpans(text, classNames = []) {
  return text.split(' ').map((t, i) => (
    <span key={i} className={`interval-${classNames[i]}`}>
      {t}
    </span>
  ));
}

const getDayInterval = ({ startDate, endDate, hideWeekends }) => {
  return {
    intervals: generateIntervals({
      startDate,
      endDate,
      startOfFirstInterval: startDate,
      hideWeekends,
      getNextDate: date => dateAdd(date, 1, { business: hideWeekends }),
    }),
    format: date => wrapInSpans(dateFormat(date, 'dd')[0], ['primary']),
    totalDays: calculateTotalDays({ startDate, endDate, hideWeekends }),
  };
};

const getWeekInterval = ({ startDate, endDate, hideWeekends }) => {
  return {
    intervals: generateIntervals({
      startDate,
      endDate,
      startOfFirstInterval: dateStartOf(
        startDate,
        hideWeekends ? 'isoWeek' : 'week'
      ),
      hideWeekends,
      getNextDate: date => dateAdd(date, 1, { period: 'week' }),
    }),
    format: (date, type) =>
      wrapInSpans(
        dateFormat(date, type === 'major' ? 'MMM D' : 'D'),
        type === 'major' ? ['secondary', 'primary'] : ['primary']
      ),
    totalDays: calculateTotalDays({ startDate, endDate, hideWeekends }),
  };
};

const getMonthInterval = ({ startDate, endDate, hideWeekends }) => {
  return {
    intervals: generateIntervals({
      startDate,
      endDate,
      startOfFirstInterval: dateStartOf(startDate, 'month'),
      hideWeekends,
      getNextDate: date => dateAdd(date, 1, { period: 'month' }),
    }),
    format: (date, type) =>
      wrapInSpans(
        dateFormat(
          date,
          type === 'major' && dateMonthIndex(date) === 0 ? 'MMM YYYY' : 'MMM'
        ),
        type === 'major' ? ['primary', 'secondary'] : ['primary']
      ),
    totalDays: calculateTotalDays({ startDate, endDate, hideWeekends }),
  };
};

const getQuarterInterval = ({
  startDate,
  endDate,
  hideWeekends,
  fiscalMonth,
  fiscalYearFormat,
}) => {
  // go back until we find a month that aligns with the fiscal month
  let startOfFirstInterval = dateStartOf(startDate, 'month');
  const fiscalMonthIndex = fiscalMonth - 1;

  while ((dateMonthIndex(startOfFirstInterval) - fiscalMonthIndex) % 3 !== 0) {
    startOfFirstInterval = dateSubtract(startOfFirstInterval, 1, {
      period: 'month',
    });
  }

  return {
    intervals: generateIntervals({
      startDate,
      endDate,
      startOfFirstInterval,
      hideWeekends,
      getNextDate: date => dateAdd(date, 3, { period: 'months' }),
    }),
    // Fiscal years starting month is configurable causing quarters not to
    // represent 'real' dates like other intervals like years, months, etc.
    // Set quarter year based on when fiscal year ends instead of the
    // quarter's year.

    format: date => {
      const str = dateToFiscalQuarterString(date, {
        fiscalMonth,
        format: fiscalYearFormat,
      });

      return wrapInSpans(
        str,
        // Testing for Q1, Q2, Q3, Q4 within the string. Depending on how we want this to work, we may want to
        // mark the "Q1" as primary and the "2023" as secondary for styling purposes, or vice versa.
        /^Q/.test(str) ? ['primary', 'secondary'] : ['secondary', 'primary']
      );
    },
    totalDays: calculateTotalDays({ startDate, endDate, hideWeekends }),
  };
};

const getYearInterval = ({
  startDate,
  endDate,
  hideWeekends,
  fiscalMonth,
  fiscalYearFormat,
}) => {
  const fiscalMonthIndex = fiscalMonth - 1;

  // year intervals must be based on the fiscal year start and displayed in the chosen
  // fiscal year format
  return {
    intervals: generateIntervals({
      startDate,
      endDate,
      // based on generateInterval logic, if the startDate is before the fiscalMonth
      // in the year, need to have the first interval start before the startDate,
      // otherwise, it skips the start of the first fiscal year
      startOfFirstInterval:
        getMonthIndex(startDate) < fiscalMonthIndex
          ? dateSubtract(
              monthYearToDate(getYear(startDate), fiscalMonthIndex),
              1,
              { period: 'year' }
            )
          : monthYearToDate(getYear(startDate), fiscalMonthIndex),
      hideWeekends,
      getNextDate: date => dateAdd(date, 1, { period: 'year' }),
    }),
    format: date =>
      wrapInSpans(
        dateToFiscalYearString(date, {
          fiscalMonth,
          format: fiscalYearFormat,
        }),
        ['primary', 'secondary']
      ),
    totalDays: calculateTotalDays({ startDate, endDate, hideWeekends }),
  };
};

export const getInterval = (key, params) => {
  switch (key) {
    case 'days':
      return getDayInterval(params);
    case 'weeks':
      return getWeekInterval(params);
    case 'months':
      return getMonthInterval(params);
    case 'quarters':
      return getQuarterInterval(params);
    case 'years':
      return getYearInterval(params);
    default:
      return getWeekInterval(params);
  }
};

export const getTotalWidthFromVisibleRange = ({
  startDate,
  endDate,
  visibleStartDate,
  visibleEndDate,
  visibleWidth,
  hideWeekends,
}) => {
  const pixelsPerDay = getPixelsPerDay({
    startDate: visibleStartDate,
    endDate: visibleEndDate,
    width: visibleWidth,
    hideWeekends,
  });

  const numDays = dateDiff(startDate, endDate, { business: hideWeekends });
  return pixelsPerDay * numDays;
};

// Renders an interval as defined by getInterval which consists of an
// intervals generator, the total number of days and a format function
// used to format a single date based on the interval type
export const renderInterval = ({
  intervalType,
  interval: { intervals, totalDays, format },
  className = null,
}) => {
  const fullWidthIntervalDays = Math.max(
    ...intervals.map(({ daysCount }) => daysCount)
  );
  return intervals.map(({ startOfInterval, daysCount }, index) => {
    let hideClippedContent = false;
    const intervalStyle = {
      width: `${(100 * daysCount) / totalDays}%`,
    };
    // Elsewhere in the custom roadmap, all interval marks are full width
    // and set to an absolute position so that they can render left of the viewport.
    // They never truncate their titles. This roadmap is built differently.
    if (daysCount / fullWidthIntervalDays < 0.5) {
      hideClippedContent = true;
    }

    return (
      <div key={startOfInterval} className={className} style={intervalStyle}>
        {hideClippedContent ? '' : format(startOfInterval, intervalType)}
      </div>
    );
  });
};

export const calculateRemainderDates = (datesToAdd, availableDates) => {
  if (Math.abs(datesToAdd) > availableDates) {
    return Math.abs(datesToAdd) - availableDates;
  }

  return 0;
};

export const calculateDateToAdd = (
  datesToAdd,
  remainderDates,
  availableDates
) => {
  const beforeSum = Math.abs(datesToAdd) + remainderDates;
  const directionalSum = datesToAdd < 0 ? beforeSum * -1 : beforeSum;

  return Math.min(directionalSum, availableDates);
};

export const ZOOM_DIRECTION = {
  IN: -1,
  OUT: 1,
};

export const calculateDatesToAddFromZoom = memoize(
  ({
    visibleStartDate,
    visibleEndDate,
    startDate,
    endDate,
    hideWeekends,
    scrollDistancePercent = 10,
    zoomDirection,
    anchorDate,
  }) => {
    if (!anchorDate) {
      // Total amount of days in the visible range.
      const totalDays = dateDiff(visibleStartDate, visibleEndDate, {
        business: hideWeekends,
      });

      // Anchor date, which is exactly the middle of the visible area.
      anchorDate = dateAdd(visibleStartDate, totalDays / 2, {
        business: hideWeekends,
      });
    }

    const totalDates = dateDiff(visibleStartDate, visibleEndDate, {
      business: hideWeekends,
    });
    const distance = Math.ceil(totalDates * (scrollDistancePercent / 100)); // Pixel distance we zoom per scroll tick, 10% proportional default

    const direction =
      zoomDirection > 0 ? ZOOM_DIRECTION.OUT : ZOOM_DIRECTION.IN;

    const numDays = distance * direction;

    const availableStartDates = dateDiff(startDate, visibleStartDate, {
      business: hideWeekends,
    });
    const availableEndDates = dateDiff(visibleEndDate, endDate, {
      business: hideWeekends,
    });

    const visibleStartDateToHoverDateDiff = dateDiff(
      visibleStartDate,
      anchorDate,
      { business: hideWeekends }
    );
    const visibleEndDateToHoverDateDiff = dateDiff(anchorDate, visibleEndDate, {
      business: hideWeekends,
    });

    // Place the number of dates to zoom in proportional to where the users mouse is over.
    // The goal should be that the same date stays under the mouse.
    const percentOfVisibleDatesBeforeHoverDate =
      visibleStartDateToHoverDateDiff / totalDates;
    const percentOfVisibleDatesAfterHoverDate =
      visibleEndDateToHoverDateDiff / totalDates;

    let baseBeforeDatesToAdd = Math.round(
      numDays * percentOfVisibleDatesBeforeHoverDate
    );

    let baseAfterDatesToAdd = Math.round(
      numDays * percentOfVisibleDatesAfterHoverDate
    );

    const startDateLeftOver = calculateRemainderDates(
      baseBeforeDatesToAdd,
      availableStartDates
    );
    const afterDateLeftOver = calculateRemainderDates(
      baseAfterDatesToAdd,
      availableEndDates
    );
    const beforeDatesToAdd = calculateDateToAdd(
      baseBeforeDatesToAdd,
      afterDateLeftOver,
      availableStartDates
    );
    const afterDatesToAdd = calculateDateToAdd(
      baseAfterDatesToAdd,
      startDateLeftOver,
      availableEndDates
    );
    // If the visible start date is before the calculated "start date", use the start date instead
    const fromDate = dateMax(
      startDate,
      // Add the needed days passed in the zoom to the visible start date, but making sure there are always atleast 1 week in view
      dateMin(
        dateAdd(visibleStartDate, beforeDatesToAdd * -1, {
          business: hideWeekends,
        }),
        dateSubtract(visibleEndDate, 7, {
          business: hideWeekends,
        })
      )
    );

    // If the visible end date is after the calculated "end date", use the end date instead
    const toDate = dateMin(
      endDate,
      // Add the calculated zoom date to the visible end date, but making sure there's a minimum 1 week in our visible view
      dateMax(
        dateAdd(visibleEndDate, afterDatesToAdd, {
          business: hideWeekends,
        }),
        dateAdd(visibleStartDate, 7, {
          business: hideWeekends,
        })
      )
    );
    const willChangeZoom =
      fromDate !== visibleStartDate || toDate !== visibleEndDate;
    return {
      beforeDatesToAdd,
      afterDatesToAdd,
      fromDate,
      toDate,
      willChangeZoom,
    };
  }
);

export const willZoom = ({
  visibleStartDate,
  visibleEndDate,
  startDate,
  endDate,
  hideWeekends,
  scrollDistancePercent,
}) => {
  const calculate = zoomDirection => {
    const { willChangeZoom } = calculateDatesToAddFromZoom({
      visibleStartDate,
      visibleEndDate,
      startDate,
      endDate,
      hideWeekends,
      scrollDistancePercent,
      zoomDirection,
    });
    return willChangeZoom;
  };
  return {
    [ZOOM_DIRECTION.IN]: calculate(ZOOM_DIRECTION.IN),
    [ZOOM_DIRECTION.OUT]: calculate(ZOOM_DIRECTION.OUT),
  };
};

interface ZoomRoadmapOptions {
  visibleStartDate: string;
  visibleEndDate: string;
  scrollDistancePercent: number;
  hideWeekends: boolean;
  startDate: string;
  endDate: string;
  setVisibleDates: (startDate: string, endDate: string) => void;
  zoomDirection: number;
  anchorDate?: string;
}

export const zoomRoadmap = ({
  visibleStartDate,
  visibleEndDate,
  scrollDistancePercent,
  hideWeekends,
  startDate,
  endDate,
  setVisibleDates,
  zoomDirection,
  anchorDate,
}: ZoomRoadmapOptions) => {
  const { fromDate, toDate, willChangeZoom } = calculateDatesToAddFromZoom({
    visibleStartDate,
    visibleEndDate,
    scrollDistancePercent,
    hideWeekends,
    startDate,
    endDate,
    zoomDirection,
    anchorDate,
  });

  setVisibleDates(fromDate, toDate);
  return { willChangeZoom };
};

export const zoomRoadmapWheelHandler = (e, scrollZoomRef, options) => {
  const { deltaX, deltaY, offsetX, target = 0 } = e;
  let currentElement = target;
  let left = offsetX;

  const { sidebarWidth = 0 } = options;

  // If we are over any element nested in the gantt bars, like milestones, release
  // dates, etc, we need to step up through the dom elements and find their position
  // from the left side to calculate the distance the mouse is horizontally.
  while (
    currentElement !== scrollZoomRef.current &&
    currentElement !== document.body
  ) {
    left += currentElement.offsetLeft;
    currentElement = currentElement.parentNode;
  }

  if (currentElement === document.body) return;
  if (deltaY === 0) return;
  if (Math.abs(deltaX) > Math.abs(deltaY)) return;

  const days = (left - sidebarWidth) / options.pixelsPerDay;

  const hoverOverDate = dateAdd(options.startDate, days, {
    business: options.hideWeekends,
  });

  zoomRoadmap({ ...options, anchorDate: hoverOverDate, zoomDirection: deltaY });
};

export const onScroll = (
  offset,
  {
    endDate,
    hideWeekends,
    onSetVisibleDatesHandler,
    startDate,
    visibleEndDate,
    visibleStartDate,
    width,
  }
) => {
  const daysWidth = dateDiff(visibleStartDate, visibleEndDate, {
    business: hideWeekends,
  });

  const totalWidth = getTotalWidthFromVisibleRange({
    startDate,
    endDate,
    visibleStartDate,
    visibleEndDate,
    visibleWidth: width,
    hideWeekends,
  });

  const newStartDate = offsetToDate({
    startDate,
    endDate,
    width: totalWidth,
    offset,
    hideWeekends,
  });

  onSetVisibleDatesHandler(
    newStartDate,
    dateAdd(newStartDate, daysWidth, { business: hideWeekends })
  );
};
